import { useCallback, useEffect, useRef } from 'react'
import Basemap from '@arcgis/core/Basemap'
import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import Map from '@arcgis/core/Map'
import { SimpleRenderer } from '@arcgis/core/renderers'
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol'
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol'
import MapView from '@arcgis/core/views/MapView'
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery'
import Expand from '@arcgis/core/widgets/Expand'
import LayerList from '@arcgis/core/widgets/LayerList'
import Legend from '@arcgis/core/widgets/Legend'

import { useGetBaseSettingsQuery } from 'app/services/api/basesettings'
import {
  getCenterlineUrl,
  getDefaultCenterCoordinates,
  getStructureLayerUrl,
  getWisTitle
} from 'common/utils/mapUtils'
import { getDetailsValueByKey } from 'common/utils/settingsUtils'

import { useEsriAuth } from './Auth'
import { IExtentQueryResponse, IMapComponent } from './interfaces/Maps'
import { buildWisDefinitionExpression } from './utils/buildWisDefinitionExpression'
import { handleDocumentClick, handleExpand } from './utils/esriWidgetHelper'
import { formatTableNode as formatTableNodeFn } from './utils/helperFuntions'
import { setupFetchInterceptor } from './utils/setupFetchInterceptor'

import './style.scss'

const DEFAULT_ZOOM = 9
const centerlineColor = [255, 255, 255, 255]
const structureColor = [81, 176, 176, 153]
const structureOutlineColor = [88, 89, 91, 255]

type ComparisonLayer = {
  layer: FeatureLayer
  type: 'baseline' | 'wis'
}

const structureFields = [
  {
    fieldName: 'StructureTypeCode',
    label: 'Structure Type'
  },
  {
    fieldName: 'ValuationUsd',
    label: 'Valuation USD'
  },
  {
    fieldName: 'HumanOccupancyFlag',
    label: 'Human Occupancy'
  },
  {
    fieldName: 'OccupantCount',
    label: 'Occupant Count'
  },
  {
    fieldName: 'TotalOutdoorOccupantCount',
    label: 'Total Outdoor Occupant Count'
  },
  {
    fieldName: 'TotalIndoorOccupantCount',
    label: 'Total Indoor Occupant Count'
  },
  {
    fieldName: 'MobilityFactor',
    label: 'Mobility Factor'
  },
  {
    fieldName: 'WoodenStructureFlag',
    label: 'Wooden Structure'
  },
  {
    fieldName: 'Units',
    label: 'Units'
  },
  {
    fieldName: 'Stories',
    label: 'Stories'
  },
  {
    fieldName: 'IdentifiedSiteFlag',
    label: 'Identified Site'
  },
  {
    fieldName: 'Longitude',
    label: 'Longitude'
  },
  {
    fieldName: 'Latitude',
    label: 'Latitude'
  }
]

const MapComponent = ({
  assessmentId,
  layer2Name,
  featureLayerTitle,
  featureLayerUrl,
  featureLayerFilterQuery,
  featureLayerFilterQueries,
  renderer,
  featureLayerUrl2,
  featureLayerTitle2,
  contentFields,
  popupExpressionInfos,
  wisServiceUrl,
  onStructureLayerVisibilityChange,
  showStructureLayer = false
}: IMapComponent) => {
  const mapDomRef = useRef() as React.MutableRefObject<HTMLDivElement>
  const dataset1LayersRef = useRef<FeatureLayer[]>([])
  const comparisonLayerRef = useRef<ComparisonLayer>()
  const structureLayerRef = useRef<FeatureLayer>()
  const showStructureLayerRef = useRef(showStructureLayer)
  const featureLayerFilterQueryRef = useRef(featureLayerFilterQuery)
  const featureLayerFilterQueriesRef = useRef(featureLayerFilterQueries)
  const rendererRef = useRef(renderer)
  const { currentData: baseSettings } = useGetBaseSettingsQuery()
  const { isEsriTokenSetupComplete } = useEsriAuth()

  const mapCenterCoordinates = getDetailsValueByKey(
    baseSettings,
    'MapSettings',
    'Center'
  )

  const centerlineUrl = getCenterlineUrl(baseSettings)
  const structureLayerUrl = getStructureLayerUrl(baseSettings)
  const hasRequiredLayerUrls = Boolean(
    featureLayerUrl?.trim() &&
      getDetailsValueByKey(baseSettings, 'MapSettings', 'Centerline') &&
      getDetailsValueByKey(baseSettings, 'MapSettings', 'StructureLayer')
  )

  const featureLayerCount = featureLayerFilterQueries?.length || 1

  featureLayerFilterQueryRef.current = featureLayerFilterQuery
  featureLayerFilterQueriesRef.current = featureLayerFilterQueries
  rendererRef.current = renderer

  useEffect(() => {
    const cleanup = setupFetchInterceptor()

    return () => cleanup() // Restore original fetch
  }, [])

  useEffect(() => {
    showStructureLayerRef.current = showStructureLayer
  }, [showStructureLayer])

  // TODO: add check to prevent 2nd rendering
  useEffect(() => {
    if (!isEsriTokenSetupComplete || !hasRequiredLayerUrls) {
      return
    }

    let cancelled = false

    const map = new Map({
      basemap: 'satellite'
    })

    const view = new MapView({
      map: map,
      center: getDefaultCenterCoordinates(mapCenterCoordinates),
      zoom: DEFAULT_ZOOM,
      spatialReference: {
        wkid: 3857
      },
      constraints: {
        minZoom: 3
      },
      container: mapDomRef.current,
      popup: {
        highlightEnabled: true,
        dockEnabled: true,
        dockOptions: {
          breakpoint: false,
          buttonEnabled: false
        }
      }
    })

    const originalBaselineRenderer = new SimpleRenderer({
      label: 'Route',
      symbol: new CIMSymbol({
        data: {
          type: 'CIMSymbolReference',
          symbol: {
            type: 'CIMLineSymbol',
            symbolLayers: [
              {
                type: 'CIMSolidStroke',
                enable: true,
                capStyle: 'Round',
                joinStyle: 'Round',
                width: 6,
                color: centerlineColor
              }
            ]
          }
        }
      })
    })

    const originalStructureRenderer = new SimpleRenderer({
      symbol: new SimpleFillSymbol({
        color: structureColor,
        outline: {
          color: structureOutlineColor,
          width: 1
        }
      })
    })

    const originalBaselineLayer = new FeatureLayer({
      title: 'Centerline',
      url: centerlineUrl,
      renderer: originalBaselineRenderer,
      definitionExpression: ''
    })

    const structureLayer = new FeatureLayer({
      title: 'Structure',
      url: structureLayerUrl,
      renderer: originalStructureRenderer,
      opacity: 0.6,
      popupTemplate: {
        title: 'Structure Info',
        content: [
          {
            type: 'fields',
            fieldInfos: structureFields
          }
        ],
        lastEditInfoEnabled: false
      },
      definitionExpression: '',
      visible: showStructureLayerRef.current
    })

    structureLayerRef.current = structureLayer

    map.add(originalBaselineLayer)
    map.add(structureLayer)

    const structureLayerVisibilityWatchHandle =
      onStructureLayerVisibilityChange != null
        ? structureLayer.watch('visible', (val) => {
            onStructureLayerVisibilityChange(val)
          })
        : undefined

    const currentFilterQueries = featureLayerFilterQueriesRef.current
    const definitionExpressions = currentFilterQueries?.length
      ? currentFilterQueries
      : [featureLayerFilterQueryRef.current ?? '']
    const baselineLayer = new FeatureLayer({
      title: featureLayerTitle,
      url: featureLayerUrl,
      renderer: rendererRef.current,
      outFields: ['*'],
      definitionExpression: definitionExpressions[0],
      listMode: 'show',
      popupTemplate: {
        title: featureLayerTitle + ' Segment Info',
        expressionInfos: popupExpressionInfos,
        content: [
          {
            type: 'fields',
            fieldInfos: contentFields
          }
        ],
        lastEditInfoEnabled: false
      }
    })

    const baselineLayers = [baselineLayer]

    dataset1LayersRef.current = baselineLayers

    const baselineLayerVisibilityWatchHandle = baselineLayer.watch(
      'visible',
      (visible) => {
        baselineLayers.slice(1).forEach((layer) => {
          layer.visible = visible
        })
      }
    )

    // Wait for the layer and view to be ready so as to ensure successful goTo navigation
    void Promise.all([baselineLayer.when(), view.when()])
      .then(() => {
        if (cancelled) {
          return undefined
        }

        const latestFilterQueries = featureLayerFilterQueriesRef.current
        const latestDefinitionExpressions = latestFilterQueries?.length
          ? latestFilterQueries
          : [featureLayerFilterQueryRef.current ?? '']

        baselineLayer.definitionExpression = latestDefinitionExpressions[0]

        latestDefinitionExpressions.slice(1).forEach((definitionExpression) => {
          const supplementalLayer = baselineLayer.clone()

          supplementalLayer.definitionExpression = definitionExpression
          supplementalLayer.listMode = 'hide'
          baselineLayers.push(supplementalLayer)
          map.add(supplementalLayer)
        })

        return Promise.all(
          baselineLayers.map((layer) => {
            const query = layer.createQuery()
            query.outSpatialReference = view.spatialReference

            return layer.queryExtent(query)
          })
        )
      })
      .then((responses: IExtentQueryResponse[] | undefined) => {
        if (cancelled || !responses) {
          return
        }

        const extent = responses.reduce<
          NonNullable<IExtentQueryResponse['extent']> | undefined
        >((combinedExtent, response) => {
          if (!response.extent) {
            return combinedExtent
          }

          return combinedExtent
            ? combinedExtent.union(response.extent)
            : response.extent.clone()
        }, undefined)

        if (!extent) {
          return view.goTo({
            center: getDefaultCenterCoordinates(mapCenterCoordinates)
          })
        }

        if (extent.center.longitude === 0 && extent.center.latitude === 0) {
          return view.goTo({
            center: getDefaultCenterCoordinates(mapCenterCoordinates)
          })
        }

        return view.goTo(extent)
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error(err)
        } else {
          console.log('Skipped view render', err)
        }
      })

    baselineLayers.forEach((layer) => map.add(layer))

    let comparisonLayer: ComparisonLayer | undefined
    let wisLayer: FeatureLayer | undefined

    if (!assessmentId) {
      if (featureLayerUrl2) {
        // 2nd Baseline layer
        const baseline2Layer = new FeatureLayer({
          title: featureLayerTitle2,
          url: featureLayerUrl2,
          renderer: rendererRef.current,
          definitionExpression: featureLayerFilterQueryRef.current,
          outFields: ['*'],
          popupTemplate: {
            title: featureLayerTitle2 + ' Segment Info',
            expressionInfos: popupExpressionInfos,
            content: [
              {
                type: 'fields',
                fieldInfos: contentFields
              }
            ]
          },
          visible: false
        })

        comparisonLayer = {
          layer: baseline2Layer,
          type: 'baseline'
        }
        comparisonLayerRef.current = comparisonLayer
        map.add(baseline2Layer)
      }
    } else {
      wisLayer = new FeatureLayer({
        title: getWisTitle(layer2Name),
        url: wisServiceUrl,
        outFields: ['*'],
        renderer: rendererRef.current,
        definitionExpression: buildWisDefinitionExpression(
          featureLayerFilterQueryRef.current,
          assessmentId
        ),
        popupTemplate: {
          title: getWisTitle(layer2Name) + ' Segment Info',
          content: [
            {
              type: 'fields',
              fieldInfos: [
                {
                  fieldName: 'RouteName',
                  label: 'Route Name'
                },
                {
                  fieldName: 'FromMeasure',
                  label: 'From Measure',
                  format: {
                    places: 2,
                    digitSeparator: true
                  }
                },
                {
                  fieldName: 'ToMeasure',
                  label: 'To Measure',
                  format: {
                    digitSeparator: true,
                    places: 2
                  }
                },
                {
                  fieldName: 'FromDate',
                  label: 'From Date'
                },
                {
                  fieldName: 'ToDate',
                  label: 'To Date'
                },
                {
                  fieldName: 'RiskTotal',
                  label: 'Total Risk',
                  format: {
                    digitSeparator: true,
                    places: 2
                  }
                },
                {
                  fieldName: 'RiskDensity',
                  label: 'Risk Density',
                  format: {
                    places: 2,
                    digitSeparator: true
                  }
                }
              ]
            }
          ]
        }
      })

      comparisonLayer = {
        layer: wisLayer,
        type: 'wis'
      }
      comparisonLayerRef.current = comparisonLayer
      map.add(wisLayer)
    }

    const getLegendLayerInfos = () => {
      let activeRiskLayer: FeatureLayer | undefined

      if (baselineLayer.visible) {
        activeRiskLayer = baselineLayer
      } else if (wisLayer?.visible) {
        activeRiskLayer = wisLayer
      }

      return [
        ...(activeRiskLayer
          ? [
              {
                layer: activeRiskLayer,
                title: 'Risk Level'
              }
            ]
          : []),
        {
          layer: structureLayer
        },
        {
          layer: originalBaselineLayer
        }
      ]
    }

    const legend = new Legend({
      view: view,
      /**
       * Note: Explicitly stating layers to show in legend to limit layers shown
       * i.e. remove 2nd baseline / WIS compare layer containg same risk breakpoints
       */
      layerInfos: getLegendLayerInfos()
    })

    const syncRiskLevelLegend = () => {
      legend.layerInfos = getLegendLayerInfos()
    }

    const riskLevelLegendWatchHandles = [
      baselineLayer.watch('visible', syncRiskLevelLegend),
      ...(wisLayer ? [wisLayer.watch('visible', syncRiskLevelLegend)] : [])
    ]

    const legendExpand = new Expand({
      view: view,
      content: legend,
      mode: 'floating'
    })

    // TODO: basemap selection persist through re-renders - low priority change
    const basemapGallerySource = [
      Basemap.fromId('satellite'),
      Basemap.fromId('hybrid'),
      Basemap.fromId('dark-gray-vector'),
      Basemap.fromId('gray-vector'),
      Basemap.fromId('terrain'),
      Basemap.fromId('streets-vector')
    ].filter((basemap) => basemap != null)

    const basemapGallery = new BasemapGallery({
      view: view,
      source: basemapGallerySource
    })

    // Create an Expand instance and set the content
    // property to the DOM node of the basemap gallery widget
    // Use an Esri icon font to represent the content inside
    // of the Expand widget
    const bgExpand = new Expand({
      view: view,
      content: basemapGallery,
      mode: 'floating'
    })

    // Layer visibility toggle
    const layerList = new LayerList({
      view: view
    })

    const layerToggleExpand = new Expand({
      view: view,
      content: layerList,
      mode: 'floating'
    })

    // Add widgets to view
    view.ui.add(legendExpand, 'top-right')
    view.ui.add(layerToggleExpand, 'top-right')
    view.ui.add(bgExpand, 'bottom-left')

    const expands = [legendExpand, layerToggleExpand, bgExpand]

    legendExpand.watch('expanded', (val) => {
      if (val) {
        handleExpand(legendExpand, expands)
      }
    })

    layerToggleExpand.watch('expanded', (val) => {
      if (val) {
        handleExpand(layerToggleExpand, expands)
      }
    })

    bgExpand.watch('expanded', (val) => {
      if (val) {
        handleExpand(bgExpand, expands)
      }
    })

    const documentClickHandler = (event: MouseEvent) => {
      handleDocumentClick(event, expands)
    }

    document.addEventListener('mousedown', documentClickHandler)

    return () => {
      cancelled = true
      structureLayerVisibilityWatchHandle?.remove()
      baselineLayerVisibilityWatchHandle.remove()
      riskLevelLegendWatchHandles.forEach((handle) => handle.remove())
      document.removeEventListener('mousedown', documentClickHandler)

      if (dataset1LayersRef.current === baselineLayers) {
        dataset1LayersRef.current = []
      }

      if (comparisonLayerRef.current === comparisonLayer) {
        comparisonLayerRef.current = undefined
      }

      if (structureLayerRef.current === structureLayer) {
        structureLayerRef.current = undefined
      }

      if (view != null) {
        view.destroy()
      }
    }
  }, [
    assessmentId,
    centerlineUrl,
    contentFields,
    featureLayerTitle,
    featureLayerTitle2,
    featureLayerUrl,
    featureLayerUrl2,
    featureLayerCount,
    hasRequiredLayerUrls,
    isEsriTokenSetupComplete,
    layer2Name,
    mapCenterCoordinates,
    onStructureLayerVisibilityChange,
    structureLayerUrl,
    wisServiceUrl,
    popupExpressionInfos
  ])

  useEffect(() => {
    const definitionExpressions = featureLayerFilterQueries?.length
      ? featureLayerFilterQueries
      : [featureLayerFilterQuery ?? '']

    dataset1LayersRef.current.forEach((layer, index) => {
      layer.definitionExpression = definitionExpressions[index] ?? ''
    })

    const comparisonLayer = comparisonLayerRef.current

    if (comparisonLayer) {
      comparisonLayer.layer.definitionExpression =
        comparisonLayer.type === 'wis' && assessmentId
          ? buildWisDefinitionExpression(featureLayerFilterQuery, assessmentId)
          : (featureLayerFilterQuery ?? '')
    }
  }, [assessmentId, featureLayerFilterQueries, featureLayerFilterQuery])

  useEffect(() => {
    if (renderer) {
      dataset1LayersRef.current.forEach((layer) => {
        layer.renderer = renderer
      })

      if (comparisonLayerRef.current) {
        comparisonLayerRef.current.layer.renderer = renderer
      }
    }
  }, [renderer])

  useEffect(() => {
    if (structureLayerRef.current) {
      structureLayerRef.current.visible = showStructureLayer
    }
  }, [showStructureLayer])

  const formatTableNode = useCallback((node: ChildNode) => {
    formatTableNodeFn(node)
  }, [])

  useEffect(() => {
    const target = document.querySelector('.esri-view-root')
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'childList' &&
          'nodeName' in mutation.target &&
          mutation.target.nodeName === 'TABLE'
        ) {
          const childNodes = mutation.addedNodes[0].childNodes
          childNodes.forEach(formatTableNode)
        }
      })
    })

    const config = { childList: true, subtree: true }

    if (target) {
      observer.observe(target, config)
    }

    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [formatTableNode, renderer])

  // check if the Centerline CIMSymbolStroke is white; update as style changes
  return (
    <div
      className={['h-full']
        .concat(
          centerlineColor.every((rgbValue) => rgbValue === 255)
            ? 'esri-white-centerline-symbol-border'
            : ''
        )
        .join(' ')}
      ref={mapDomRef}
      id='mapDiv'
    />
  )
}

export default MapComponent
